/**
 * Body event handler registry
 * Manages event listeners on document.body to prevent duplicate handlers
 * Allows multiple different handlers for the same event type
 */

const bodyEvents = {
  // Map structure: eventType -> Set of handlers
  listeners: new Map(),

  /**
   * Add a body event listener (prevents duplicate handlers)
   * @param {string} eventType - The event type (e.g., 'click', 'resize')
   * @param {Function} handler - The event handler function
   * @param {boolean | AddEventListenerOptions} options - Optional event listener options
   * @returns {boolean} - True if handler was added, false if it already existed
   */
  add(eventType, handler, options = {}) {
    if (!document.body) {
      console.warn('bodyEvents: document.body is not available');
      return false;
    }

    // Get or create the Set of handlers for this event type
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }

    const handlers = this.listeners.get(eventType);

    // Check if this specific handler is already registered
    if (handlers.has(handler)) {
      return false; // Handler already exists, don't add again
    }

    // Add the handler
    document.body.addEventListener(eventType, handler, options);
    handlers.add(handler);
    return true;
  },

  /**
   * Remove a specific body event listener
   * @param {string} eventType - The event type
   * @param {Function} handler - The event handler function to remove
   * @param {boolean | AddEventListenerOptions} options - Optional event listener options
   * @returns {boolean} - True if handler was removed, false if it didn't exist
   */
  remove(eventType, handler, options = {}) {
    const handlers = this.listeners.get(eventType);

    if (!handlers || !handlers.has(handler)) {
      return false;
    }

    document.body.removeEventListener(eventType, handler, options);
    handlers.delete(handler);

    // Clean up empty Sets
    if (handlers.size === 0) {
      this.listeners.delete(eventType);
    }

    return true;
  },

  /**
   * Check if a specific handler exists for an event type
   * @param {string} eventType - The event type
   * @param {Function} handler - The event handler function
   * @returns {boolean} - True if the handler is registered
   */
  has(eventType, handler) {
    const handlers = this.listeners.get(eventType);
    return handlers ? handlers.has(handler) : false;
  },

  /**
   * Get count of handlers for an event type (useful for debugging)
   * @param {string} eventType - The event type
   * @returns {number} - Number of handlers registered
   */
  count(eventType) {
    const handlers = this.listeners.get(eventType);
    return handlers ? handlers.size : 0;
  }
};

export default bodyEvents;
